/**
 * 表格查询同步工具
 * 统一 xmTagSearch(或) / xmQueryForm+列头(且) / xmGridMoreQuery 之间的字段同步
 */
import queryFormCtlType from '@/main-page/data/queryFormCtlType';
import {initFilterValueByType} from '@/main-page/utils/domainQueryUtils';
import eventBus from '@/events/eventBus';
import GoingUtils from '@/utils/goingUtils';
import {deepClone} from 'xmgoing-utils';
import {
  beginQuerySyncEcho,
  consumeAfterInitQuerySyncEcho,
  isSameQueryField,
  parseQueryParamField,
  peekQuerySyncEcho,
  preferPrefixedQueryField,
  reconcileDateRangeParams,
  shouldSkipAfterInitUiEcho,
  shouldSkipColQueryEcho as shouldSkipColQueryEchoBySource,
  shouldSkipMoreQueryEcho as shouldSkipMoreQueryEchoBySource,
  shouldSkipQueryFormEcho as shouldSkipQueryFormEchoBySource,
} from '@/main-page/utils/dateRangeQuerySync';
import type {QuerySyncEcho} from '@/main-page/utils/dateRangeQuerySync';

export {
  beginQuerySyncEcho,
  consumeAfterInitQuerySyncEcho,
  isSameQueryField,
  parseQueryParamField,
  peekQuerySyncEcho,
  preferPrefixedQueryField,
  shouldSkipAfterInitUiEcho,
};

/** 列头 filterType → xmGridMoreQuery 控件类型 */
const FILTER_TYPE_TO_QUERY_CTL: Record<string, string> = {
  inputFilter: 'xmInput',
  selFilter: 'xmSelect',
  dateRangeFilter: 'xmDateRange',
  relDomainFilter: 'EnumRel',
  enumDomainFilter: 'EnumRel',
  dataSelectFilter: 'xmDataSelect',
  userSelListFilter: 'UserSelect',
};

/** xmTagSearch 条件操作符 → 查询操作符 */
const TAG_CONDITION_TO_OPERATOR: Record<string, string> = {
  container: 'like',
  notContainer: 'notLike',
  equals: 'eq',
  notEquals: 'nq',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',
  greater: 'gt',
  greaterOrEquals: 'ge',
  less: 'lt',
  lessOrEquals: 'le',
  startWith: 'startWith',
  endWith: 'endWith',
};

/** 同步时忽略的参数字段 */
const IGNORED_PARAM_KEYS = new Set([
  'current',
  'size',
  'paginationSize',
  'orderList',
  'moreQueryConfigs',
  'paramsRel',
  'sPageCode_',
]);

export interface ParsedQueryItem {
  fieldName: string;
  operator: string;
  value: any;
  logicOperator: 'and' | 'or';
}

/** 查询条件同步来源 */
export type GridQuerySyncSource = 'grid' | 'column' | 'form';

/** 查询 patch 来源 */
export type GridQueryPatchSource = 'form' | 'column' | 'moreQuery' | 'tag';

export interface GridQueryPatch {
  source: GridQueryPatchSource;
  layer: 'and' | 'or';
  triggerParams?: Record<string, any>;
  filterParams?: { filter: any[]; filterRelVal: string };
  filterQueryParams?: any[];
  tagKey?: string;
  initCurrent?: boolean;
}

/** 查询表单实时值读取器（gridKey → 当前控件值） */
const formLiveParamsRegistry = new Map<string, () => Record<string, any> | null | undefined>();

/** 查询表单当前选中已保存查询方案的 gridKey（内置默认方案不入此集合） */
const savedSchemeQueryFormGridKeys = new Set<string>();

export function setQueryFormSavedSchemeIsolation(gridKey: string, isolated: boolean) {
  if (!gridKey) {
    return;
  }
  if (isolated) {
    savedSchemeQueryFormGridKeys.add(gridKey);
  } else {
    savedSchemeQueryFormGridKeys.delete(gridKey);
  }
}

export function isQueryFormOnSavedScheme(gridKey: string): boolean {
  return !!gridKey && savedSchemeQueryFormGridKeys.has(gridKey);
}

/** 列头/筛选回写查询表单：来源为表单自身，或已保存方案隔离下的列头/高级筛选 */
export function shouldSkipQueryFormEcho(echo: QuerySyncEcho | null | undefined, gridKey?: string) {
  if (shouldSkipQueryFormEchoBySource(echo)) {
    return true;
  }
  if (!gridKey || !isQueryFormOnSavedScheme(gridKey)) {
    return false;
  }
  return echo?.source === 'column' || echo?.source === 'moreQuery';
}

/** 回写列头：来源为列头自身，或已保存方案隔离下的查询表单 */
export function shouldSkipColQueryEcho(echo: QuerySyncEcho | null | undefined, gridKey?: string) {
  if (shouldSkipColQueryEchoBySource(echo)) {
    return true;
  }
  return !!(gridKey && isQueryFormOnSavedScheme(gridKey) && echo?.source === 'form');
}

/** 回写高级筛选：来源为面板/标签，或已保存方案隔离下的查询表单 */
export function shouldSkipMoreQueryEcho(echo: QuerySyncEcho | null | undefined, gridKey?: string) {
  if (shouldSkipMoreQueryEchoBySource(echo)) {
    return true;
  }
  return !!(gridKey && isQueryFormOnSavedScheme(gridKey) && echo?.source === 'form');
}

export function registerGridQueryFormLiveParams(
  gridKey: string,
  getter: () => Record<string, any> | null | undefined
) {
  if (!gridKey) {
    return;
  }
  formLiveParamsRegistry.set(gridKey, getter);
}

export function unregisterGridQueryFormLiveParams(gridKey: string) {
  if (!gridKey) {
    return;
  }
  formLiveParamsRegistry.delete(gridKey);
}

/**
 * 读取查询表单当前控件值（含未点「查询」的草稿）
 */
export function getFormLiveQueryParams(gridKey: string): Record<string, any> {
  if (!gridKey) {
    return {};
  }
  const getter = formLiveParamsRegistry.get(gridKey);
  if (!getter) {
    return {};
  }
  const vals = getter();
  return vals && typeof vals === 'object' ? deepClone(vals) : {};
}

export interface InitQueryCondPayload {
  queryParams: Record<string, any>;
  syncSource?: GridQuerySyncSource;
}

/**
 * 构建 _initQueryCond 事件载荷
 */
export function buildInitQueryCondPayload(
  queryParams: Record<string, any>,
  syncSource: GridQuerySyncSource = 'grid'
): InitQueryCondPayload {
  return {queryParams, syncSource};
}

/**
 * 兼容旧版直接传 queryParams 对象的事件载荷
 */
export function normalizeInitQueryCondPayload(payload: any): InitQueryCondPayload {
  if (payload?.queryParams && typeof payload.queryParams === 'object') {
    return {
      queryParams: payload.queryParams,
      syncSource: payload.syncSource || 'grid',
    };
  }
  return {queryParams: payload || {}, syncSource: 'grid'};
}

/**
 * 判断列是否开启了列头查询
 */
export function isColHeaderFilterEnabled(col: any): boolean {
  return col && (col.showFilter === true || col.showFilter === 'true');
}

/**
 * 获取可列头查询的列
 */
export function getColHeaderFilterColumns(cols: any[]): any[] {
  if (!cols || !Array.isArray(cols)) {
    return [];
  }
  return cols.filter((col) => {
    return isColHeaderFilterEnabled(col) &&
      col.controlType !== 'orderNum' &&
      col.controlType !== 'operation';
  });
}

/**
 * 根据列头 filterType 获取 xmGridMoreQuery 控件类型
 */
export function getQueryCtlTypeFromColumn(col: any): string {
  if (!col) {
    return 'xmInput';
  }
  if (col.filterType && FILTER_TYPE_TO_QUERY_CTL[col.filterType]) {
    return FILTER_TYPE_TO_QUERY_CTL[col.filterType];
  }
  return queryFormCtlType[col.controlType || col.propType] || 'xmInput';
}

/**
 * 为 xmGridMoreQuery 查询项补充列头查询控件配置
 */
export function enrichColForMoreQuery(col: any): any {
  if (!col) {
    return col;
  }
  const queryCtlType = getQueryCtlTypeFromColumn(col);
  return {
    ...col,
    queryCtlType,
    componentKey: queryCtlType,
  };
}

/**
 * 解析高级查询/列头共用的规范查询键（优先过滤字段 filterField）
 */
export function resolveQueryFieldKey(col: any): string {
  if (!col) {
    return '';
  }
  return col.filterField || col.dataIndex || col.propId || col.propName || '';
}

/**
 * 按查询 id 反查列（支持 filterField、表别名形式如 t.actualStartTime）
 */
export function matchColumnByQueryId(cols: any[] | undefined | null, queryId: string): any | undefined {
  if (!cols?.length || queryId === undefined || queryId === null || queryId === '') {
    return undefined;
  }
  const id = String(queryId);
  const bareId = id.includes('.') ? id.split('.').pop()! : id;

  return cols.find((col) => {
    if (!col) {
      return false;
    }
    if (col.propId === id || col.dataIndex === id || col.propId === bareId || col.dataIndex === bareId) {
      return true;
    }
    if (col.filterField === id || col.filterField === bareId) {
      return true;
    }
    if (
      typeof col.filterField === 'string' &&
      (col.filterField.endsWith('.' + id) || col.filterField.endsWith('.' + bareId))
    ) {
      return true;
    }
    const key = resolveQueryFieldKey(col);
    return key === id || key === bareId;
  });
}

/** 列头下拉/多选类 filterType（人员筛选单独处理单选/多选） */
const MULTI_VALUE_FILTER_TYPES = new Set([
  'selFilter',
  'dataSelectFilter',
  'relDomainFilter',
  'enumDomainFilter',
]);

/** 列头/高级筛选：人员选择、下拉过滤是否为单选 */
export function isUserFilterSingleSelect(col: any): boolean {
  const single =
    col?.filterUserSingleSelect === true ||
    col?.filterUserSingleSelect === 'true' ||
    col?.props?.filterUserSingleSelect === true ||
    col?.props?.filterUserSingleSelect === 'true';
  const filterType = col?.filterType || col?.props?.filterType;
  return single && (filterType === 'userSelListFilter' || filterType === 'selFilter');
}

/** 是否为下拉过滤列（列头 selFilter 或高级筛选 xmSelect） */
export function isSelFilterColumn(col: any): boolean {
  const filterType = col?.filterType || col?.props?.filterType;
  if (filterType === 'selFilter') {
    return true;
  }
  return (col?.componentKey || col?.queryCtlType) === 'xmSelect';
}

/** 是否为日期/区间类列（xmDateRange 等） */
export function isDateRangeColumn(col: any): boolean {
  if (!col) {
    return false;
  }
  if (col.filterType === 'dateRangeFilter') {
    return true;
  }
  const ctlType = getQueryCtlTypeFromColumn(col);
  return (ctlType + '').indexOf('Range') >= 0 || ctlType === 'xmDateRange';
}

const hasQueryValue = (value: any) => value !== undefined && value !== null && value !== '';

function removeFieldQueryParams(params: Record<string, any>, fieldName: string) {
  Object.keys(params).forEach((key) => {
    if (IGNORED_PARAM_KEYS.has(key)) {
      return;
    }
    const parsed = parseQueryParamField(key);
    if (isSameQueryField(parsed.fieldName, fieldName) || isSameQueryField(key, fieldName)) {
      delete params[key];
    }
  });
}

/**
 * 将 moreQueryConfigs 回写为 flat 参数。日期 between 拆成 _ge/_le，避免查询表单/列头仍读到旧区间。
 */
export function applyMoreQueryConfigsToFlatParams(params: Record<string, any>) {
  if (!params) {
    return params;
  }
  let configs = params.moreQueryConfigs;
  if (typeof configs === 'string') {
    try {
      configs = JSON.parse(configs);
    } catch {
      return params;
    }
  }
  if (!configs?.searchField?.length) {
    return params;
  }
  configs.searchField.forEach((fieldJson: any) => {
    const fieldName = fieldJson?.key;
    if (!fieldName) {
      return;
    }
    removeFieldQueryParams(params, fieldName);
    if (!hasQueryValue(fieldJson.value)) {
      return;
    }
    if (fieldJson.operator === 'between') {
      const parts = String(fieldJson.value).split(';').map((item) => String(item).trim());
      if (parts[0]) {
        params[`${fieldName}_ge`] = parts[0];
      }
      if (parts[1]) {
        params[`${fieldName}_le`] = parts[1];
      } else if (parts[0]) {
        params[`${fieldName}_le`] = parts[0];
      }
      return;
    }
    const operator = fieldJson.operator || 'like';
    params[operator === 'like' ? fieldName : `${fieldName}_${operator}`] = fieldJson.value;
  });
  return params;
}

const WIRE_KEEP_PARAM_KEYS = new Set([
  ...IGNORED_PARAM_KEYS,
  'sGridKey_',
]);

function findMoreQuerySearchFieldIndex(
  configs: {searchField: any[]} | null | undefined,
  fieldName: string
) {
  if (!configs?.searchField?.length) {
    return -1;
  }
  return configs.searchField.findIndex((fieldJson: any) => {
    if (!fieldJson?.key) {
      return false;
    }
    return isSameQueryField(fieldJson.key, fieldName)
      || isSameQueryField(parseQueryParamField(fieldJson.key).fieldName, fieldName);
  });
}

function resolveMoreQueryFieldKey(fieldName: string, cols?: any[]) {
  if (!fieldName) {
    return fieldName;
  }
  if (!cols?.length) {
    return fieldName;
  }
  const col = matchColumnByQueryId(cols, fieldName);
  if (!col) {
    return fieldName;
  }
  return preferPrefixedQueryField(resolveQueryFieldKey(col), fieldName);
}

function upsertMoreQuerySearchField(
  configs: {searchField: any[]; logicOperator?: string},
  fieldName: string,
  operator: string,
  value: any
) {
  const existingIndex = findMoreQuerySearchFieldIndex(configs, fieldName);
  const existingKey = existingIndex > -1 ? configs.searchField[existingIndex].key : '';
  const nextItem = {
    key: preferPrefixedQueryField(existingKey, fieldName),
    value,
    operator,
    type: 'STRING',
  };
  if (existingIndex > -1) {
    configs.searchField[existingIndex] = {
      ...configs.searchField[existingIndex],
      ...nextItem,
    };
  } else {
    configs.searchField.push(nextItem);
  }
}

function removeMoreQuerySearchField(
  configs: {searchField: any[]} | null | undefined,
  fieldName: string
) {
  const existingIndex = findMoreQuerySearchFieldIndex(configs, fieldName);
  if (existingIndex > -1) {
    configs!.searchField.splice(existingIndex, 1);
  }
}

/**
 * 将 flat 查询字段并入 moreQueryConfigs（筛选面板线格式发请求前用）
 */
export function foldFlatQueryParamsIntoMoreQueryConfigs(
  params: Record<string, any>,
  cols?: any[]
) {
  if (!params) {
    return params;
  }
  const fieldGroups = new Map<string, Array<{operator: string; value: any}>>();
  Object.keys(params).forEach((key) => {
    if (WIRE_KEEP_PARAM_KEYS.has(key)) {
      return;
    }
    if (!hasQueryValue(params[key])) {
      return;
    }
    const {fieldName, operator} = parseQueryParamField(key);
    const resolvedName = resolveMoreQueryFieldKey(fieldName, cols);
    if (!fieldGroups.has(resolvedName)) {
      fieldGroups.set(resolvedName, []);
    }
    fieldGroups.get(resolvedName)!.push({operator, value: params[key]});
  });
  if (!fieldGroups.size) {
    return params;
  }

  let configs = params.moreQueryConfigs;
  if (typeof configs === 'string') {
    try {
      configs = JSON.parse(configs);
    } catch {
      configs = {searchField: []};
    }
  }
  if (!configs || !Array.isArray(configs.searchField)) {
    configs = {searchField: []};
  }

  fieldGroups.forEach((items, fieldName) => {
    const geItem = items.find((item) => item.operator === 'ge');
    const leItem = items.find((item) => item.operator === 'le');
    if (geItem || leItem) {
      const start = geItem?.value ?? leItem?.value;
      const end = leItem?.value ?? geItem?.value;
      upsertMoreQuerySearchField(configs, fieldName, 'between', `${start};${end}`);
      return;
    }
    const last = items[items.length - 1];
    upsertMoreQuerySearchField(configs, fieldName, last.operator || 'like', last.value);
  });
  params.moreQueryConfigs = configs;
  return params;
}

/**
 * 查询表单 / 列头 last-writer：只把本次 trigger 字段写回 moreQueryConfigs。
 * 不 fold 全部 flat，避免把查询表单未提交草稿同步进筛选面板。
 */
function reconcileMoreQueryConfigsWithLastWriter(
  params: Record<string, any>,
  triggerParams?: Record<string, any> | null,
  cols?: any[]
) {
  if (!params?.moreQueryConfigs || !triggerParams) {
    return params;
  }
  let configs = params.moreQueryConfigs;
  if (typeof configs === 'string') {
    try {
      configs = JSON.parse(configs);
    } catch {
      return params;
    }
    params.moreQueryConfigs = configs;
  }
  if (!configs || !Array.isArray(configs.searchField)) {
    return params;
  }

  const fieldGroups = new Map<string, Array<{operator: string; value: any}>>();
  const emptyFields = new Set<string>();
  Object.keys(triggerParams).forEach((key) => {
    if (WIRE_KEEP_PARAM_KEYS.has(key) || key === 'moreQueryConfigs') {
      return;
    }
    const {fieldName, operator} = parseQueryParamField(key);
    const resolvedName = resolveMoreQueryFieldKey(fieldName, cols);
    if (!hasQueryValue(triggerParams[key])) {
      emptyFields.add(resolvedName);
      return;
    }
    if (!fieldGroups.has(resolvedName)) {
      fieldGroups.set(resolvedName, []);
    }
    fieldGroups.get(resolvedName)!.push({operator, value: triggerParams[key]});
  });

  fieldGroups.forEach((items, fieldName) => {
    emptyFields.delete(fieldName);
    const geItem = items.find((item) => item.operator === 'ge');
    const leItem = items.find((item) => item.operator === 'le');
    if (geItem || leItem) {
      const start = geItem?.value ?? leItem?.value;
      const end = leItem?.value ?? geItem?.value;
      upsertMoreQuerySearchField(configs, fieldName, 'between', `${start};${end}`);
      return;
    }
    const last = items[items.length - 1];
    upsertMoreQuerySearchField(configs, fieldName, last.operator || 'like', last.value);
  });
  emptyFields.forEach((fieldName) => {
    removeMoreQuerySearchField(configs, fieldName);
  });
  if (!configs.searchField.length) {
    delete params.moreQueryConfigs;
  }
  return params;
}

function stripFlatQueryFieldParams(params: Record<string, any>) {
  Object.keys(params).forEach((key) => {
    if (!WIRE_KEEP_PARAM_KEYS.has(key)) {
      delete params[key];
    }
  });
}

/**
 * AND 层发往接口的线格式（与四入口改版前一致）：
 * - 筛选面板 / 标签 AND：只传 moreQueryConfigs，不传字段 flat
 * - 查询表单 / 列头：只传 flat，不传 moreQueryConfigs
 * 内部同步仍用完整合并结果，勿直接拿本函数结果去回写 UI。
 */
export function toAndLayerWireQueryParams(
  params: Record<string, any> | null | undefined,
  source: GridQueryPatchSource,
  cols?: any[]
): Record<string, any> {
  const wire = deepClone(params || {});
  if (source === 'moreQuery' || source === 'tag') {
    foldFlatQueryParamsIntoMoreQueryConfigs(wire, cols);
    stripFlatQueryFieldParams(wire);
    return wire;
  }
  if (wire.moreQueryConfigs) {
    applyMoreQueryConfigsToFlatParams(wire);
  }
  delete wire.moreQueryConfigs;
  return wire;
}

/**
 * 将 xmQueryForm / 列头的 _ge + _le 参数合并为高级筛选的 between 值
 */
export function resolveDateRangeInitQuery(
  queryList: Array<{ queryId: string; operatorStr: string; value: any }>
): { queryValue: string; operatorStr: 'between' } | null {
  const geQuery = queryList.find((q) => q.operatorStr === 'ge');
  const leQuery = queryList.find((q) => q.operatorStr === 'le');
  const geVal = hasQueryValue(geQuery?.value) ? String(geQuery!.value) : '';
  const leVal = hasQueryValue(leQuery?.value) ? String(leQuery!.value) : '';

  if (geVal || leVal) {
    if (geVal && leVal) {
      return {queryValue: `${geVal};${leVal}`, operatorStr: 'between'};
    }
    return {queryValue: geVal || leVal, operatorStr: 'between'};
  }

  const betweenQuery = queryList.find((q) => q.operatorStr === 'between');
  if (betweenQuery && hasQueryValue(betweenQuery.value)) {
    return {queryValue: String(betweenQuery.value), operatorStr: 'between'};
  }

  const plainQuery = queryList.find((q) => q.operatorStr === 'like' || !q.operatorStr);
  if (plainQuery && hasQueryValue(plainQuery.value)) {
    const val = String(plainQuery.value);
    if (val.includes(';')) {
      return {queryValue: val, operatorStr: 'between'};
    }
  }
  return null;
}

/**
 * 根据列头 filterType 推断默认查询操作符（与列头查询行为一致）
 */
export function getDefaultOperatorForColumn(col: any, operatorStr?: string): string {
  if (!col) {
    return operatorStr || 'like';
  }
  if (isUserFilterSingleSelect(col)) {
    const allowed = ['eq', 'nq'];
    if (operatorStr && allowed.includes(operatorStr)) {
      return operatorStr;
    }
    return 'eq';
  }
  if (col.filterType === 'userSelListFilter') {
    return operatorStr || 'eq';
  }
  if (isDateRangeColumn(col) && operatorStr && (operatorStr === 'ge' || operatorStr === 'le')) {
    return 'between';
  }
  if (operatorStr) {
    return operatorStr;
  }
  if (col.filterType === 'dateRangeFilter') {
    return 'between';
  }
  if (MULTI_VALUE_FILTER_TYPES.has(col.filterType)) {
    return 'in';
  }
  const ctlType = getQueryCtlTypeFromColumn(col);
  if ((ctlType + '').indexOf('Range') >= 0) {
    return 'between';
  }
  return 'like';
}

/**
 * 列头过滤能否以自身控件写出该查询关系（文本列为包含/等于；人员单选为等于/不等于等）
 */
export function canColumnExpressOperator(col: any, operator?: string): boolean {
  const op = operator || 'like';
  if (!col) {
    return op === 'like' || op === 'eq';
  }
  if (isUserFilterSingleSelect(col)) {
    return op === 'eq' || op === 'nq';
  }
  if (col.filterType === 'userSelListFilter') {
    return op === 'eq' || op === 'nq' || op === 'in';
  }
  if (isDateRangeColumn(col)) {
    return op === 'between' || op === 'ge' || op === 'le';
  }
  if (MULTI_VALUE_FILTER_TYPES.has(col.filterType)) {
    return op === 'in' || op === 'eq' || op === 'nq';
  }
  return op === 'like' || op === 'eq';
}

/**
 * 为更多筛选查询项设置与列头一致的控件 props（下拉选项、多选等）
 */
export function applyMoreQueryItemProps(col: any, queryItem: any): void {
  if (!col || !queryItem) {
    return;
  }
  const filterType = col.filterType;
  if (filterType === 'selFilter' && col.filterList?.length) {
    queryItem.dataConfig = {dataList: col.filterList};
    queryItem.props = queryItem.props || {};
    const single = isUserFilterSingleSelect(col);
    queryItem.props.multiple = !single;
    if (single) {
      queryItem.filterUserSingleSelect = true;
      queryItem.props.filterUserSingleSelect = true;
    }
  } else if (filterType === 'userSelListFilter') {
    queryItem.props = queryItem.props || {};
    const single = isUserFilterSingleSelect(col);
    queryItem.props.multiple = !single;
    queryItem.props.filterUserSingleSelect = single;
    if (col.filterByUserName === true || col.filterByUserName === 'true') {
      queryItem.props.filterByUserName = true;
    }
    if (queryItem.ctlFromType === 'more-search-list') {
      queryItem.props.showSelIcon = true;
    }
  } else if (MULTI_VALUE_FILTER_TYPES.has(filterType)) {
    queryItem.props = queryItem.props || {};
    queryItem.props.multiple = true;
    if (queryItem.componentKey === 'EnumRel') {
      queryItem.props.showType = 'select';
    }
  }
  if (filterType === 'dateRangeFilter') {
    queryItem.operator = 'between';
  }
}

/** 筛选面板 Popover 层级（低于 Modal 默认 1000，避免遮挡选人窗口） */
export const XM_GRID_MORE_QUERY_POPOVER_CLASS = 'xm-grid-more-query-popover';
export const XM_GRID_MORE_QUERY_FIELD_POPOVER_CLASS = 'xm-grid-more-query-field-popover';

/**
 * xmTagSearch 条件操作符映射
 */
export function mapTagConditionToOperator(condition?: string): string {
  if (!condition) {
    return 'eq';
  }
  return TAG_CONDITION_TO_OPERATOR[condition] || condition;
}

/**
 * 解析 xmTagSearch 单个 filter 的值
 */
export function resolveTagFilterValue(filterItem: any, pageModelsStore?: any): any {
  if (!filterItem) {
    return '';
  }
  const propItem = {...filterItem};
  if (propItem.valueType) {
    initFilterValueByType({
      valueType: propItem.valueType,
      propItem,
      pageModelsStore,
      filterValue: propItem.filterValue,
      filterSubValue: propItem.filterSubValue,
    });
  }
  return propItem.filterValue ?? '';
}

/**
 * 扁平化 xmTagSearch 的 filter 树，提取叶子条件
 */
export function flattenTagFilters(filters: any[]): any[] {
  const result: any[] = [];
  if (!filters || !Array.isArray(filters)) {
    return result;
  }
  filters.forEach((item) => {
    if (item?.children?.length > 0) {
      result.push(...flattenTagFilters(item.children));
    } else if (item?.propId || item?.paraName) {
      result.push(item);
    }
  });
  return result;
}

/**
 * 从 xmTagSearch filterParams 解析 OR 查询项
 */
export function parseTagFilterToQueryItems(
  filterParams: any,
  cols: any[],
  pageModelsStore?: any
): ParsedQueryItem[] {
  if (!filterParams?.filter?.length) {
    return [];
  }
  const filterableCols = getColHeaderFilterColumns(cols);
  const flatFilters = flattenTagFilters(filterParams.filter);
  const items: ParsedQueryItem[] = [];

  flatFilters.forEach((filterItem) => {
    const fieldName = filterItem.propId || filterItem.paraName;
    if (!fieldName) {
      return;
    }
    const col = matchColumnByQueryId(filterableCols, fieldName);
    if (!col) {
      return;
    }
    const operator = mapTagConditionToOperator(filterItem.condition);
    const value = resolveTagFilterValue(filterItem, pageModelsStore);
    items.push({
      fieldName,
      operator,
      value,
      logicOperator: (filterParams.filterRelVal === 'or' ? 'or' : 'and') as 'and' | 'or',
    });
  });
  return items;
}

/**
 * 解析 flat queryParams（xmQueryForm / 列头查询）为 AND 查询项
 */
export function parseFlatParamsToQueryItems(params: any, cols: any[]): ParsedQueryItem[] {
  if (!params || typeof params !== 'object') {
    return [];
  }
  const filterableCols = getColHeaderFilterColumns(cols);
  const fieldGroupMap = new Map<string, Array<{ operator: string; value: any }>>();

  Object.keys(params).forEach((fullQueryId) => {
    if (IGNORED_PARAM_KEYS.has(fullQueryId)) {
      return;
    }
    const {fieldName, operator} = parseQueryParamField(fullQueryId);
    const value = params[fullQueryId];

    if (!fieldGroupMap.has(fieldName)) {
      fieldGroupMap.set(fieldName, []);
    }
    fieldGroupMap.get(fieldName)!.push({operator, value});
  });

  const items: ParsedQueryItem[] = [];
  fieldGroupMap.forEach((queryList, fieldName) => {
    const col = matchColumnByQueryId(filterableCols, fieldName);
    if (!col) {
      return;
    }
    const validQueries = queryList.filter((q) => q.value !== undefined && q.value !== '' && q.value !== null);
    if (validQueries.length > 0) {
      items.push({
        fieldName: resolveQueryFieldKey(col) || fieldName,
        operator: validQueries[0].operator,
        value: validQueries[0].value,
        logicOperator: 'and',
      });
    }
  });
  return items;
}

/**
 * 解析 moreQueryConfigs 中的查询项
 */
export function parseMoreQueryConfigsToItems(moreQueryConfigs: any): ParsedQueryItem[] {
  if (!moreQueryConfigs?.searchField?.length) {
    return [];
  }
  return moreQueryConfigs.searchField.map((fieldJson: any) => ({
    fieldName: fieldJson.key,
    operator: fieldJson.operator || 'like',
    value: fieldJson.value,
    logicOperator: (moreQueryConfigs.logicOperator === 'or' ? 'or' : 'and') as 'and' | 'or',
  }));
}

/**
 * 合并 AND + OR 查询项（去重：同字段 AND 优先于 OR）
 */
export function mergeQueryItems(
  andItems: ParsedQueryItem[],
  orItems: ParsedQueryItem[]
): ParsedQueryItem[] {
  const andFieldSet = new Set(andItems.map((i) => i.fieldName));
  const filteredOrItems = orItems.filter((i) => !andFieldSet.has(i.fieldName));
  return [...andItems, ...filteredOrItems];
}

/**
 * 是否为 OR 关系查询
 */
export function isOrQueryRelation(paramsRel?: string): boolean {
  return paramsRel === 'or';
}

/**
 * 继承刷新时保持 OR 查询层。
 * flushGrid(..., extendParams=true, 'and') 在页面脚本里多半是刷新默认值，不是 OR→AND 切层；
 * 若上一轮是 OR，须继续带 paramsRel=or，且不要把 OR 字段回写到表头。
 * 真正切到 AND 请用 extendParams=false；继承旧 OR 条件再追加 AND 参数暂不处理。
 */
export function applyExtendFlushOrLayerPreserve(
  tableObj: any,
  params?: Record<string, any>,
  inheritedQueryParams?: Record<string, any>
) {
  const inheritedRel = inheritedQueryParams?.paramsRel;
  if (
    !isOrQueryRelation(tableObj?.paramsRel) &&
    !isOrQueryRelation(params?.paramsRel) &&
    !isOrQueryRelation(inheritedRel)
  ) {
    return;
  }
  if (tableObj) {
    tableObj.paramsRel = 'or';
  }
  if (params) {
    params.paramsRel = 'or';
  }
}

/**
 * 解析表格当前条件关系（优先 tableObj.paramsRel）
 */
export function resolveGridParamsRel(
  gridTableStore: any,
  gridKey: string,
  dataConfig?: any
): string | undefined {
  const tableObj = gridTableStore?.getGridObjByKey?.(gridKey);
  return tableObj?.paramsRel || dataConfig?.paramsRel || undefined;
}

/**
 * 导出参数补充 paramsRel（OR 查询时必须带上）
 */
export function appendParamsRelToExportParams(
  params: Record<string, any>,
  gridTableStore: any,
  gridKey: string,
  dataConfig?: any
): Record<string, any> {
  const paramsRel = resolveGridParamsRel(gridTableStore, gridKey, dataConfig);
  if (isOrQueryRelation(paramsRel)) {
    params.paramsRel = 'or';
  }
  return params;
}

/**
 * OR 查询后的列头查询：不继承旧条件，仅保留排序
 */
export function buildColQueryParamsWithoutOrInheritance(
  oldParams: Record<string, any> | null | undefined,
  newParams: Record<string, any>
): Record<string, any> {
  const paramsNew = {...newParams};
  if (oldParams?.orderList && !newParams.orderList) {
    paramsNew.orderList = oldParams.orderList;
  }
  return paramsNew;
}

/**
 * ParsedQueryItem → flat queryParams
 */
export function parsedQueryItemsToFlatParams(items: ParsedQueryItem[]): Record<string, any> {
  const params: Record<string, any> = {};
  items.forEach((item) => {
    if (item.value === undefined || item.value === null || item.value === '') {
      return;
    }
    const key = item.operator === 'like' ? item.fieldName : `${item.fieldName}_${item.operator}`;
    params[key] = item.value;
  });
  return params;
}

/**
 * ParsedQueryItem → xmGridMoreQuery moreQueryConfigs
 */
export function parsedQueryItemsToMoreQueryConfigs(
  items: ParsedQueryItem[],
  logicOperator: 'and' | 'or'
): { searchField: any[]; logicOperator: 'and' | 'or' } {
  return {
    searchField: items
      .filter((item) => item.value !== undefined && item.value !== null && item.value !== '')
      .map((item) => ({
        key: item.fieldName,
        value: item.value,
        operator: item.operator || 'like',
        type: 'STRING',
      })),
    logicOperator,
  };
}

function isRangeOperator(operator: string) {
  return operator === 'ge' || operator === 'le';
}

/**
 * 同一业务字段只能保留一种查询形态。后写入的 key 生效时，清掉同字段其它后缀
 *（如 materialProperty_in 覆盖无后缀 materialProperty / materialProperty_like）。
 * 日期区间 _ge/_le 需要成对存在，不在这里互删。
 */
function removeSiblingFieldOperatorParams(params: Record<string, any>, paramKey: string) {
  const {fieldName, operator} = parseQueryParamField(paramKey);
  if (!fieldName || isRangeOperator(operator)) {
    return;
  }
  Object.keys(params).forEach((key) => {
    if (key === paramKey || IGNORED_PARAM_KEYS.has(key) || key === 'moreQueryConfigs') {
      return;
    }
    const parsed = parseQueryParamField(key);
    if (isSameQueryField(parsed.fieldName, fieldName) && !isRangeOperator(parsed.operator)) {
      delete params[key];
    }
  });
}

function mergeQueryParamObjects(
  target: Record<string, any>,
  source: Record<string, any> | null | undefined,
  options?: { replaceMoreQueryConfigs?: boolean }
) {
  if (!source || typeof source !== 'object') {
    return;
  }
  if (source.moreQueryConfigs !== undefined) {
    if (options?.replaceMoreQueryConfigs) {
      if (source.moreQueryConfigs && source.moreQueryConfigs.searchField?.length) {
        target.moreQueryConfigs = deepClone(source.moreQueryConfigs);
      } else {
        delete target.moreQueryConfigs;
      }
    } else if (source.moreQueryConfigs?.searchField?.length) {
      target.moreQueryConfigs = deepClone(source.moreQueryConfigs);
    }
  }
  Object.keys(source).forEach((key) => {
    if (key === 'moreQueryConfigs') {
      return;
    }
    if (IGNORED_PARAM_KEYS.has(key)) {
      if (source[key] !== undefined) {
        target[key] = source[key];
      }
      return;
    }
    if (source[key] === undefined || source[key] === null || source[key] === '') {
      delete target[key];
    } else {
      removeSiblingFieldOperatorParams(target, key);
      target[key] = source[key];
    }
  });
}

/**
 * AND 层参数合并：隐式已提交 → 表单草稿 → 本次触发（last-writer）
 */
export function buildAndLayerQueryParams(
  gridKey: string,
  triggerParams: Record<string, any> | null | undefined,
  source: GridQueryPatchSource,
  gridTableStore: any
): Record<string, any> {
  const tableObj = gridTableStore?.getGridObjByKey?.(gridKey);
  const merged: Record<string, any> = {};

  if (source !== 'column') {
    mergeQueryParamObjects(merged, tableObj?.committedColQueryParams || {});
  }

  if (source !== 'moreQuery' && source !== 'tag' && tableObj?.committedMoreQueryConfigs) {
    merged.moreQueryConfigs = deepClone(tableObj.committedMoreQueryConfigs);
  }

  if (source !== 'form') {
    mergeQueryParamObjects(merged, getFormLiveQueryParams(gridKey));
  }

  mergeQueryParamObjects(merged, triggerParams || {}, {
    replaceMoreQueryConfigs: source === 'moreQuery' || source === 'tag',
  });

  delete merged.paramsRel;
  const gridCols = gridTableStore?.[gridKey]?.cols || gridTableStore?.getGridCols?.(gridKey) || [];
  const hadMoreQueryConfigs = !!merged.moreQueryConfigs?.searchField?.length;
  if (source === 'moreQuery' || source === 'tag') {
    applyMoreQueryConfigsToFlatParams(merged);
  } else if (hadMoreQueryConfigs) {
    reconcileMoreQueryConfigsWithLastWriter(merged, triggerParams, gridCols);
  }
  reconcileDateRangeParams(merged, source, triggerParams);
  if (gridTableStore?.setCommittedMoreQueryConfigs) {
    if (merged.moreQueryConfigs?.searchField?.length) {
      gridTableStore.setCommittedMoreQueryConfigs(gridKey, merged.moreQueryConfigs);
    } else if (source === 'moreQuery' || source === 'tag' || hadMoreQueryConfigs) {
      gridTableStore.setCommittedMoreQueryConfigs(gridKey, null);
    }
  }
  if (gridTableStore?.setCommittedColQueryParams && tableObj) {
    const colParams = {...(tableObj.committedColQueryParams || {})};
    Object.keys(colParams).forEach((key) => {
      if ((key.endsWith('_ge') || key.endsWith('_le')) && !Object.prototype.hasOwnProperty.call(merged, key)) {
        delete colParams[key];
      }
    });
    Object.keys(merged).forEach((key) => {
      if (key.endsWith('_ge') || key.endsWith('_le')) {
        colParams[key] = merged[key];
      }
    });
    gridTableStore.setCommittedColQueryParams(gridKey, colParams);
  }
  return merged;
}

/**
 * 取消标签激活态
 */
export function resetTagSearchActive(gridKey: string) {
  if (!gridKey) {
    return;
  }
  eventBus.emit(GoingUtils.getEventCtlKey(gridKey) + '_resetTagSearchActive', {});
}

/**
 * 退出 OR 查询层
 */
export function exitOrLayer(gridKey: string, gridTableStore: any) {
  if (!gridKey) {
    return;
  }
  clearTagFilterParams(gridTableStore, gridKey);
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  if (tableObj) {
    tableObj.paramsRel = 'and';
    tableObj.activeTagKey = null;
  }
  gridTableStore.setCommittedMoreQueryConfigs(gridKey, null);
  gridTableStore.setCommittedColQueryParams(gridKey, {});
  resetTagSearchActive(gridKey);
}

/**
 * 进入 OR 查询层（标签 OR / 高级筛选 OR）
 */
export function applyOrLayerQueryPatch(
  gridKey: string,
  patch: GridQueryPatch,
  gridTableStore: any,
  pageModelsStore?: any
) {
  if (!gridKey || patch.layer !== 'or') {
    return;
  }
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  if (!tableObj) {
    return;
  }

  tableObj.paramsRel = 'or';
  tableObj.committedColQueryParams = {};
  tableObj.committedMoreQueryConfigs = null;

  const eventKey = GoingUtils.getEventCtlKey(gridKey);
  let syncParams: Record<string, any> = {paramsRel: 'or'};
  let flushParams: Record<string, any> = {};

  if (patch.source === 'tag' && patch.filterParams) {
    tableObj.filterParams = patch.filterParams;
    tableObj.filterQueryParams = patch.filterQueryParams || [];
    tableObj.activeTagKey = patch.tagKey ?? null;

    const cols = gridTableStore[gridKey]?.cols || [];
    const items = parseTagFilterToQueryItems(patch.filterParams, cols, pageModelsStore);
    syncParams.moreQueryConfigs = parsedQueryItemsToMoreQueryConfigs(items, 'or');
    flushParams = {};
  } else if (patch.source === 'moreQuery') {
    tableObj.filterParams = null;
    tableObj.filterQueryParams = [];
    tableObj.activeTagKey = null;
    resetTagSearchActive(gridKey);

    flushParams = deepClone(patch.triggerParams || {});
    flushParams.paramsRel = 'or';
    syncParams = {...flushParams};
    if (flushParams.moreQueryConfigs) {
      tableObj.committedMoreQueryConfigs = deepClone(flushParams.moreQueryConfigs);
    }
  }

  clearColHeaderAndQueryForm(gridKey);
  eventBus.emit(eventKey + '_initQueryCond', buildInitQueryCondPayload(syncParams, 'grid'));

  gridTableStore.flushGrid(
    gridKey,
    flushParams,
    patch.initCurrent !== false,
    false,
    'or'
  );
}

/**
 * 将上一标签已同步、但新标签未覆盖的字段写入空值，便于表单/列头清空
 */
export function appendClearedTagSyncFields(
  target: Record<string, any>,
  prevItems: ParsedQueryItem[],
  newItems: ParsedQueryItem[]
) {
  const newFieldSet = new Set(newItems.map((item) => item.fieldName));
  prevItems.forEach((item) => {
    if (newFieldSet.has(item.fieldName)) {
      return;
    }
    target[item.fieldName] = '';
    if (item.operator && item.operator !== 'like') {
      target[`${item.fieldName}_${item.operator}`] = '';
    }
    target[`${item.fieldName}_like`] = '';
    target[`${item.fieldName}_eq`] = '';
    target[`${item.fieldName}_in`] = '';
  });
}

/**
 * 标签 AND → AND 查询层
 */
export function applyAndLayerFromTag(
  gridKey: string,
  tagPayload: {
    filter: any[];
    filterRelVal: string;
    queryUserParams?: any[];
    tagKey?: string;
  },
  gridTableStore: any,
  pageModelsStore?: any
) {
  if (!gridKey) {
    return;
  }
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  if (!tableObj) {
    return;
  }

  if (isOrQueryRelation(tableObj.paramsRel)) {
    exitOrLayer(gridKey, gridTableStore);
  }

  clearTagFilterParams(gridTableStore, gridKey);

  const cols = gridTableStore[gridKey]?.cols || [];
  const filterParams = {filter: tagPayload.filter, filterRelVal: tagPayload.filterRelVal || 'and'};
  tableObj.filterParams = filterParams;
  const prevItems = parseMoreQueryConfigsToItems(tableObj.committedMoreQueryConfigs || {searchField: []});
  const items = parseTagFilterToQueryItems(filterParams, cols, pageModelsStore);
  const moreQueryConfigs = parsedQueryItemsToMoreQueryConfigs(items, 'and');
  const triggerParams = {
    ...parsedQueryItemsToFlatParams(items),
    moreQueryConfigs,
  };
  appendClearedTagSyncFields(triggerParams, prevItems, items);

  if (tagPayload.queryUserParams?.length) {
    tableObj.filterQueryParams = tagPayload.queryUserParams;
  } else {
    tableObj.filterQueryParams = [];
  }

  tableObj.activeTagKey = tagPayload.tagKey ?? null;
  if (triggerParams.moreQueryConfigs?.searchField?.length) {
    tableObj.committedMoreQueryConfigs = deepClone(triggerParams.moreQueryConfigs);
  } else {
    gridTableStore.setCommittedMoreQueryConfigs(gridKey, null);
  }

  const merged = buildAndLayerQueryParams(gridKey, triggerParams, 'tag', gridTableStore);
  const formSyncParams = {...merged};
  appendClearedTagSyncFields(formSyncParams, prevItems, items);
  syncGridQueryComponents(gridKey, formSyncParams, gridTableStore);
  gridTableStore.flushGrid(gridKey, toAndLayerWireQueryParams(merged, 'tag', gridTableStore[gridKey]?.cols), true, false, 'and');
}

/**
 * OR 查询刷新列表时：清空表头筛选与 xmQueryForm（不同步 OR 字段到表头/表单）
 */
export function clearColHeaderAndQueryForm(gridKey: string) {
  if (!gridKey) {
    return;
  }
  const eventKey = GoingUtils.getEventCtlKey(gridKey);
  eventBus.emit(eventKey + '_resetQuery', {});
  eventBus.emit(eventKey + '_initQueryForm', {});
}

/**
 * 清空 xmTagSearch 的 filterParams
 */
export function clearTagFilterParams(gridTableStore: any, gridKey: string) {
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  if (tableObj) {
    tableObj.filterParams = null;
    tableObj.filterQueryParams = [];
  }
}

/**
 * 清空 xmTagSearch 中 xmQueryInput 的输入框
 */
export function clearTagSearchInput(gridKey: string) {
  if (!gridKey) {
    return;
  }
  eventBus.emit(GoingUtils.getEventCtlKey(gridKey) + '_clearTagSearchInput', {});
}

/**
 * 重置 xmTagSearch 标签侧状态（输入框 / 激活态 / filterParams），供互斥场景调用。
 * 返回 reset 前标签已生效的条件项，便于清空曾同步到表单/列头的字段。
 */
export function resetTagSearchWidget(
  gridKey: string,
  gridTableStore: any,
  options?: { resetActive?: boolean; clearInput?: boolean }
): ParsedQueryItem[] {
  if (!gridKey || !gridTableStore) {
    return [];
  }
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  const hadTagState = !!(tableObj?.filterParams || tableObj?.activeTagKey);
  let prevItems: ParsedQueryItem[] = [];
  // 只收集真正由标签生效的字段。筛选面板写入的 committedMoreQueryConfigs
  // 不能当标签条件清掉，否则表头改 closeStatus 会把表单/面板一并清空。
  if (hadTagState) {
    if (tableObj?.filterParams) {
      const cols = gridTableStore[gridKey]?.cols || [];
      prevItems = parseTagFilterToQueryItems(tableObj.filterParams, cols);
    } else if (tableObj?.committedMoreQueryConfigs?.searchField?.length) {
      prevItems = parseMoreQueryConfigsToItems(tableObj.committedMoreQueryConfigs);
    }
  }

  if (options?.clearInput !== false) {
    clearTagSearchInput(gridKey);
  }
  if (options?.resetActive !== false) {
    resetTagSearchActive(gridKey);
  }
  clearTagFilterParams(gridTableStore, gridKey);
  if (tableObj) {
    tableObj.activeTagKey = null;
  }
  if (hadTagState) {
    gridTableStore.setCommittedMoreQueryConfigs(gridKey, null);
  }
  return prevItems;
}

/**
 * 构建用于同步的查询参数
 */
export function buildSyncQueryParams(
  gridTableStore: any,
  gridKey: string,
  params: Record<string, any> | null | undefined,
  extendParams = false
): Record<string, any> {
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  let syncParams = deepClone(params || {});
  if (extendParams && tableObj?.queryParams) {
    syncParams = {...deepClone(tableObj.queryParams), ...syncParams};
  }
  delete syncParams.current;
  return syncParams;
}

/**
 * 同步 xmQueryForm、列头查询、xmGridMoreQuery 之间的查询状态
 * xmTagSearch 的 OR 条件通过 tableObj.filterParams 由 _initQueryCond 读取
 */
export function syncGridQueryComponents(
  gridKey: string,
  params: Record<string, any> | null | undefined,
  gridTableStore: any,
  options?: { extendParams?: boolean }
) {
  if (!gridKey) {
    return;
  }
  const syncParams = buildSyncQueryParams(
    gridTableStore,
    gridKey,
    params,
    options?.extendParams
  );
  const eventKey = GoingUtils.getEventCtlKey(gridKey);
  const tableObj = gridTableStore.getGridObjByKey(gridKey);
  const paramsRel = syncParams.paramsRel || tableObj?.paramsRel;
  const echo = peekQuerySyncEcho(tableObj);

  if (isOrQueryRelation(paramsRel)) {
    clearColHeaderAndQueryForm(gridKey);
    if (!shouldSkipMoreQueryEcho(echo, gridKey)) {
      eventBus.emit(eventKey + '_initQueryCond', buildInitQueryCondPayload(syncParams, 'grid'));
    }
    return;
  }

  if (!shouldSkipQueryFormEcho(echo, gridKey)) {
    eventBus.emit(eventKey + '_initQueryForm', syncParams);
  }
  if (!shouldSkipMoreQueryEcho(echo, gridKey)) {
    eventBus.emit(eventKey + '_initQueryCond', buildInitQueryCondPayload(syncParams, 'grid'));
  }
  if (!shouldSkipColQueryEcho(echo, gridKey)) {
    eventBus.emit(eventKey + '_syncColQuery', syncParams);
  }
}

/** 页面上已挂载 xmGridMoreQuery 的 gridKey 集合 */
const gridMoreQueryRegistry = new Set<string>();

export function registerGridMoreQuery(gridKey: string) {
  if (gridKey) {
    gridMoreQueryRegistry.add(gridKey);
  }
}

export function unregisterGridMoreQuery(gridKey: string) {
  if (gridKey) {
    gridMoreQueryRegistry.delete(gridKey);
  }
}

export function hasGridMoreQuery(gridKey: string): boolean {
  return !!gridKey && gridMoreQueryRegistry.has(gridKey);
}

/**
 * 查询表单点「查询」后，页面脚本常用 getFormVuexVal，不含表头条件。
 * 仅在 echo=form 时只读补回已提交列头参数，不回写 committed、不碰高级筛选。
 */
export function mergeCommittedAndLayerIntoFlushParams(
  gridKey: string,
  params: Record<string, any> | null | undefined,
  gridTableStore: any,
  options?: {extendParams?: boolean; paramsRel?: string}
): Record<string, any> {
  const nextParams = params && typeof params === 'object' ? {...params} : {};
  if (options?.extendParams || !gridKey) {
    return nextParams;
  }
  const tableObj = gridTableStore?.getGridObjByKey?.(gridKey);
  if (peekQuerySyncEcho(tableObj)?.source !== 'form') {
    return nextParams;
  }
  const paramsRel = options?.paramsRel || nextParams.paramsRel || tableObj?.paramsRel;
  if (isOrQueryRelation(paramsRel)) {
    return nextParams;
  }
  const colParams = tableObj?.committedColQueryParams;
  if (!colParams || typeof colParams !== 'object') {
    return nextParams;
  }
  Object.keys(colParams).forEach((key) => {
    if (IGNORED_PARAM_KEYS.has(key)) {
      return;
    }
    const colVal = colParams[key];
    if (colVal === undefined || colVal === null || colVal === '') {
      return;
    }
    const formVal = nextParams[key];
    if (formVal !== undefined && formVal !== null && formVal !== '') {
      return;
    }
    nextParams[key] = colVal;
  });
  return nextParams;
}

/**
 * Toolbar 关键字查询前：清空其它四入口已生效条件，不发列表请求（请求由 search 事件执行）
 */
export function resetOtherEntriesBeforeToolbarSearch(gridKey: string, gridTableStore: any) {
  if (!gridKey || !gridTableStore) {
    return;
  }
  resetTagSearchWidget(gridKey, gridTableStore, {clearInput: false});
  if (isOrQueryRelation(gridTableStore.getGridObjByKey(gridKey)?.paramsRel)) {
    exitOrLayer(gridKey, gridTableStore);
  }
  gridTableStore.setCommittedColQueryParams(gridKey, {});
  gridTableStore.setCommittedMoreQueryConfigs(gridKey, null);
  clearColHeaderAndQueryForm(gridKey);
  syncGridQueryComponents(gridKey, {}, gridTableStore);
}

